import { countries } from '@aerapass/country-data';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  IconDefinition,
  faBolt,
  faChessKnight,
  faCodeBranch,
  faCubes,
  faEye,
  faGear,
  faHardHat,
  faHouse,
  faImage,
  faLeaf,
  faMagnifyingGlass,
  faPalette,
  faPeopleRoof,
  faPlug,
  faSave,
  faSpinner,
  faTimes,
  faTrash,
  faUndo,
  faUpload,
  faUsers,
} from '@fortawesome/pro-solid-svg-icons';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ColDef, GridApi, ICellRendererParams, IRowNode } from 'ag-grid-community';
import Keycloak from 'keycloak-js';
import { findIndex } from 'lodash';
import { MessageService } from 'primeng/api';
import { Breadcrumb } from 'primeng/breadcrumb';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { ColorPicker } from 'primeng/colorpicker';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { FileUpload } from 'primeng/fileupload';
import { FloatLabel } from 'primeng/floatlabel';
import { InputSwitch } from 'primeng/inputswitch';
import { InputText } from 'primeng/inputtext';
import { Password } from 'primeng/password';
import { Toast } from 'primeng/toast';
import { Observable, Subject, auditTime, combineLatest, filter, map, startWith, takeUntil } from 'rxjs';
import { AgnosticGridsComponent } from 'src/app/components/agnostic-grid/agnostic-grids/agnostic-grids.component';
import { AgButtonCellParams } from 'src/app/components/agnostic-grid/components/agButtonCell/models/ag-button-cell-params.model';
import { AgButtonCellListComponent } from 'src/app/components/agnostic-grid/components/agButtonCellList/ag-button-cell-list.component';
import { AgEditCellComponent } from 'src/app/components/agnostic-grid/components/agEditCell/agEditCell.component';
import { GridData } from 'src/app/components/agnostic-grid/models/gridData-model';
import { CompanyCategory } from 'src/app/models/company/company-category.model';
import { Company, CompanyModule, ContactPerson } from 'src/app/models/company/company-models.model';
import { FileDocument } from 'src/app/models/company/document.model';
import { CompanyProfileService } from 'src/app/services/company/company-profile.service';
import { CompanyService } from 'src/app/services/company/company.service';
import { DocumentService } from 'src/app/services/company/document.service';
import { RestService } from 'src/app/services/rest.service';
import { RgsHttpErrorResponse } from 'src/app/shared/models/http-error.model';
import { ActionType } from 'src/app/shared/services/common-state.service';
import { PermissionsService, globalRolePermissionsParams } from 'src/app/shared/services/permissions.service';
import { ArrayUtils } from 'src/app/shared/utils/array.utils';
import { FormUtils } from 'src/app/shared/utils/form.utils';
import { RxJSUtils } from 'src/app/shared/utils/rxjs.utils';
import { ToastUtils } from 'src/app/shared/utils/toast.utils';
import { v4 as uuidv4 } from 'uuid';
import { AgnosticGridsComponent as AgnosticGridsComponent_1 } from '../../../../../../components/agnostic-grid/agnostic-grids/agnostic-grids.component';
import { ErrorsPipe } from '../../../../../../shared/pipes/errors.pipe';
import { IncludesPipe } from '../../../../../../shared/pipes/includes.pipe';
import { IsRequiredPipe } from '../../../../../../shared/pipes/is-required.pipe';
import { AddNewButtonComponent } from '../../../../standalone/addNewButton/addNewButton.component';
import { LicenseService } from '../../license/services/license.service';

interface CategoryOption {
  value: CompanyCategory;
  name: string;
  icon: IconDefinition;
  color: string;
}
interface ModuleOption {
  value: CompanyModule;
  name: string;
  icon: IconDefinition;
  color: string;
}

const titleColor = '#0070C0';
const subtitleColor = '#002060';
const tableHeaderColor = '#B3C8DC';
const fontColor = '#000000';

@Component({
  changeDetection: ChangeDetectionStrategy.Default,
  selector: 'app-company',
  templateUrl: './company.component.html',
  styleUrls: ['./company.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    Card,
    Breadcrumb,
    Button,
    FloatLabel,
    InputText,
    FileUpload,
    InputSwitch,
    Checkbox,
    FaIconComponent,
    AddNewButtonComponent,
    AgnosticGridsComponent_1,
    ColorPicker,
    Password,
    Dialog,
    ConfirmDialog,
    Toast,
    AsyncPipe,
    TranslateModule,
    IsRequiredPipe,
    ErrorsPipe,
    IncludesPipe,
  ],
})
export class CompanyComponent implements OnInit, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);
  private readonly companyService = inject(CompanyService);
  private readonly route = inject(ActivatedRoute);
  private readonly companyProfileService = inject(CompanyProfileService);
  private readonly documentService = inject(DocumentService);
  private readonly translateService = inject(TranslateService);
  private readonly restService = inject(RestService);
  private readonly router = inject(Router);
  private readonly keycloak = inject(Keycloak);
  readonly permissionsService = inject(PermissionsService);
  private readonly toastService = inject(MessageService);
  private readonly licenseService = inject(LicenseService);
  private readonly cdr = inject(ChangeDetectorRef);

  public readonly faSave = faSave;
  public readonly faTrash = faTrash;
  public readonly faUndo = faUndo;
  public readonly faTimes = faTimes;
  public readonly faImage = faImage;
  public readonly faPalette = faPalette;
  public readonly faGear = faGear;
  public readonly faUpload = faUpload;
  public readonly faSpinner = faSpinner;
  public readonly faPlug = faPlug;

  public readonly companyBackgroundImageSrc = signal<string | undefined>(undefined);
  public readonly loadingCompanyBackground = signal(false);
  public readonly uploadingCompanyBackground = signal(false);
  public readonly deletingCompanyBackground = signal(false);

  public readonly loadingLogo = signal(false);
  public readonly uploadingLogo = signal(false);
  public readonly deletingLogo = signal(false);
  public readonly savingContactPerson = signal(false);

  private readonly A4_PORTRAIT_ASPECT_RATIO = 210 / 297;
  private readonly A4_ASPECT_TOLERANCE = 0.03;
  public readonly A4_MIN_WIDTH_PX = 1240;
  public readonly A4_MIN_HEIGHT_PX = 1754;
  public readonly A4_OPTIMAL_WIDTH = 2480;
  public readonly A4_OPTIMAL_HEIGHT = 3508;

  public categories: CategoryOption[] = [
    {
      value: CompanyCategory.ENGINEERING,
      name: this.translateService.instant('company.categories.' + CompanyCategory.ENGINEERING),
      icon: faHardHat,
      color: '',
    },
    {
      value: CompanyCategory.GREEN_INFRASTRUCTURE,
      name: this.translateService.instant('company.categories.' + CompanyCategory.GREEN_INFRASTRUCTURE),
      icon: faLeaf,
      color: '',
    },
    {
      value: CompanyCategory.INSTALLATION_TECHNOLOGY,
      name: this.translateService.instant('company.categories.' + CompanyCategory.INSTALLATION_TECHNOLOGY),
      icon: faBolt,
      color: '',
    },
  ];

  public modules: ModuleOption[] = [
    {
      value: CompanyModule.OBJECT,
      name: this.translateService.instant('modules.' + CompanyModule.OBJECT),
      icon: faHouse,
      color: 'rgb(54, 53, 52)',
    },
    {
      value: CompanyModule.STRATEGY,
      name: this.translateService.instant('modules.' + CompanyModule.STRATEGY),
      icon: faChessKnight,
      color: 'rgb(192, 0, 0)',
    },
    {
      value: CompanyModule.TEAM,
      name: this.translateService.instant('modules.' + CompanyModule.TEAM),
      icon: faUsers,
      color: 'rgb(255, 102, 0)',
    },
    {
      value: CompanyModule.STRUCTURE,
      name: this.translateService.instant('modules.' + CompanyModule.STRUCTURE),
      icon: faCubes,
      color: 'rgb(255, 192, 0)',
    },
    {
      value: CompanyModule.INSPECTION,
      name: this.translateService.instant('modules.' + CompanyModule.INSPECTION),
      icon: faEye,
      color: 'rgba(126, 194, 52)',
    },
    {
      value: CompanyModule.SCENARIO,
      name: this.translateService.instant('modules.' + CompanyModule.SCENARIO),
      icon: faCodeBranch,
      color: 'rgba(48, 112, 228)',
    },
  ];

  public extraModules: ModuleOption[] = [
    {
      value: CompanyModule.ROOF_PRESERVATION,
      name: this.translateService.instant('modules.' + CompanyModule.ROOF_PRESERVATION),
      icon: faPeopleRoof,
      color: 'rgba(0, 176, 240)',
    },
    {
      value: CompanyModule.DETAIL_INSPECTION,
      name: this.translateService.instant('modules.' + CompanyModule.DETAIL_INSPECTION),
      icon: faMagnifyingGlass,
      color: 'rgb(128, 0, 128)',
    },
  ];

  public titleColor: string = titleColor;
  public subtitleColor: string = subtitleColor;
  public tableHeaderColor: string = tableHeaderColor;
  public fontColor: string = fontColor;

  public reportColorPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

  public formGroup: FormGroup = this.formBuilder.group({
    naam: ['', Validators.required],
    address: [''],
    postcode: [''],
    place: [''],
    mailingAddress: [''],
    mailingPostcode: [''],
    mailingPlace: [''],
    tlf: ['', [Validators.pattern('[- +()0-9]+'), Validators.minLength(8)]],
    email: ['', [Validators.email]],
    website: ['', []],
    logo: [''],
    modules: [[]],
    categorien: [[]],
    licentie: ['', [Validators.required]],
    isopdrachtgever: [false],
    afasId: [''],
    afasToken: [''],
    uploadReportsIntegration: [false],
    exactApiKey: [''],
    exactUrl: ['https://heimdall.bouw7.nl'],
    itrisUsername: [''],
    itrisPassword: [''],
  });
  public contactPersonFormGroup: FormGroup = this.formBuilder.group({
    voornaam: [''],
    achternaam: ['', [Validators.required]],
    functie: [''],
    mobiel: ['', [Validators.pattern('[- +()0-9]+'), Validators.minLength(8)]],
    email: ['', [Validators.email]],
  });
  public reportColorFormGroup = this.formBuilder.group({
    titleColor: [
      this.titleColor,
      Validators.compose([Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/), Validators.required]),
    ],
    subtitleColor: [
      this.subtitleColor,
      Validators.compose([Validators.pattern(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/), Validators.required]),
    ],
  });

  public gridDependencies!: GridData<ContactPerson>;
  public imageSrc: any;
  public showForm: boolean = true;
  public contactPersonDialogOpen: boolean = false;
  public toggleButtonTitle: string = 'Contact Persons';
  private companyLogoObj!: FileDocument;
  public countryNames: string[] = [];
  private currentEditingRow: any;

  public destroy$ = new Subject<void>();
  public gridInstance!: AgnosticGridsComponent;
  public loading$ = RxJSUtils.someLoading(
    this.companyService.loadingSelected$,
    this.companyService.loadingAction$,
    this.companyProfileService.loadingSelected$,
    this.companyProfileService.loadingAction$,
  );
  public companyId$: Observable<string | undefined> = this.route.params.pipe(
    map((params) => (params['id'] === 'add' ? undefined : params['id'])),
  );
  public mode: 'company-profile' | 'company-admin' = 'company-profile';
  public company$: Observable<Company> = combineLatest([
    this.companyService.selected$.pipe(startWith(undefined)),
    this.companyProfileService.selected$.pipe(startWith(undefined)),
  ]).pipe(
    map(([company, companyProfile]) => {
      if (company && !this.route.snapshot.url.some((segment) => segment.path === 'companyProfile')) {
        this.mode = 'company-admin';
        return company;
      } else if (this.route.snapshot.params['id'] !== 'add') {
        this.mode = 'company-profile';
        return companyProfile as unknown as Company;
      } else {
        this.mode = 'company-admin';
        return undefined;
      }
    }),
    filter((company) => !!company),
  ) as Observable<Company>;

  private currentCompany!: Company;
  public personGridApi!: GridApi<ContactPerson>;

  public user$ = this.permissionsService.user$;

  @ViewChild(AgnosticGridsComponent) public set gridInstanceChild(gridInstance: AgnosticGridsComponent) {
    if (gridInstance) {
      this.gridInstance = gridInstance;
    }
  }
  constructor() {
    countries.all.forEach((country) => {
      this.countryNames.push(country.name);
    });
  }

  public ngOnInit() {
    this.companyService.action$
      .pipe(
        takeUntil(this.destroy$),
        auditTime(100),
        filter(
          (action) =>
            (action.type !== ActionType.LOAD_ALL &&
              action.type !== ActionType.LOAD_SELECTED &&
              action.type !== ActionType.UPDATE_MINOR) ||
            action.status === 'error',
        ),
      )
      .subscribe((action) => {
        ToastUtils.handleAction(action, 'companies', this.translateService, this.toastService, 'company-profile');

        if (action.type === ActionType.CREATE) {
          this.router.navigate(['../'], { relativeTo: this.route });
        }
      });

    this.companyProfileService.loadCompanyProfile();
    this.companyId$.subscribe((companyId) => {
      if (companyId) {
        this.companyService.loadSelected(companyId);
      }
    });

    this.reportColorFormGroup.controls.titleColor.valueChanges.subscribe((value) => {
      this.reportColorFormGroup.controls.titleColor.markAllAsTouched();
      this.reportColorFormGroup.controls.titleColor.markAsDirty();
      this.reportColorFormGroup.controls.titleColor.setValue(value, { onlySelf: true, emitEvent: false });
    });
    this.reportColorFormGroup.controls.subtitleColor.valueChanges.subscribe((value) => {
      this.reportColorFormGroup.controls.subtitleColor.setValue(value, { onlySelf: true, emitEvent: false });
    });

    this.company$.subscribe((company) => {
      if (!company?.id) {
        return;
      }

      this.currentCompany = company;

      this.gridDependencies = {
        rawJsonData: ArrayUtils.ensureArray(company?.contactpersonen),
        structure: this.contactStructure(),
        componentName: 'companyComponent',
      };

      const parsedData = {
        naam: company.naam,
        email: company.adres?.email,
        postcode: company.adres?.postcode,
        place: company.adres?.plaats,
        tlf: company.adres?.telefoon,
        website: company.adres?.website,
        address: company.adres?.straat,
        mailingAddress: company.postadres?.straat,
        mailingPostcode: company.postadres?.postcode,
        mailingPlace: company.postadres?.plaats,
        logo: company.logo,
        modules: company.modules,
        categorien: company.categorien,
        licentie: company.licentie,
        isopdrachtgever: company.isopdrachtgever,
        afasToken: this.permissionsService.licence?.integrations?.AFAS?.apiToken,
        afasId: this.permissionsService.licence?.integrations?.AFAS?.administrationId,
        exactApiKey: this.permissionsService.licence?.integrations?.EXACT?.apiKey,
        exactUrl: this.permissionsService.licence?.integrations?.EXACT?.baseUrl,
        uploadReportsIntegration: this.permissionsService.licence?.uploadReportsIntegration,
        itrisUsername: this.permissionsService.licence?.integrations?.ITRIS?.username,
        itrisPassword: this.permissionsService.licence?.integrations?.ITRIS?.password,
      };

      this.reportColorFormGroup.patchValue({
        titleColor: company.titleColor ? company.titleColor : this.titleColor,
        subtitleColor: company.subtitleColor ? company.subtitleColor : this.subtitleColor,
      });

      this.formGroup.patchValue(parsedData);
      FormUtils.markAllAsDirtyAndTouched(this.formGroup);

      if (company?.logo) {
        this.loadingLogo.set(true);
        this.restService.get(`/document/${company.logo}`).subscribe({
          next: (fileDocument: FileDocument) => {
            this.loadingLogo.set(false);
            if (fileDocument && fileDocument?.content) {
              this.companyLogoObj = fileDocument;
              this.imageSrc = 'data:image/jpg;base64,' + fileDocument.content;
              this.cdr.detectChanges();
            }
          },
          error: () => {
            this.loadingLogo.set(false);
          },
        });
      } else {
        this.imageSrc = undefined;
      }

      if (company?.companyBackgroundImageId) {
        this.loadingCompanyBackground.set(true);
        this.restService
          .get(`/document/${company.companyBackgroundImageId}`)
          .subscribe((fileDocument: FileDocument) => {
            this.loadingCompanyBackground.set(false);
            if (fileDocument && fileDocument?.content) {
              this.companyBackgroundImageSrc.set(
                `data:${fileDocument.inhoudtype || 'image/png'};base64,${fileDocument.content}`,
              );
              this.cdr.detectChanges();
            }
          });
      } else {
        this.companyBackgroundImageSrc.set(undefined);
      }

      if (
        this.mode === 'company-profile' ||
        this.hasPermission('write', this.globalRolePermissionsParams(this.permissionsService.user, 'companyProfile'))
      ) {
        this.formGroup.enable();
        this.formGroup.get('licentie')?.disable();
      } else {
        this.formGroup.disable();
      }

      this.formGroup.controls['licentie'].disable();
    });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.companyService.unloadSelected();
  }

  private contactStructure() {
    const colDefs: ColDef<ContactPerson>[] = [
      {
        field: 'voornaam',
        headerName: this.translateService.instant('fields.firstName'),
        flex: 1,
        filter: true,
        resizable: true,
        suppressMovable: true,
        sortable: false,
      },
      {
        field: 'achternaam',
        headerName: this.translateService.instant('fields.lastName'),
        flex: 1,
        filter: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
      },
      {
        field: 'functie',
        headerName: this.translateService.instant('fields.function'),
        flex: 1,
        filter: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
      },
      {
        field: 'mobiel',
        headerName: this.translateService.instant('fields.mobile'),
        flex: 1,
        filter: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
      },
      {
        field: 'email',
        headerName: this.translateService.instant('fields.email'),
        flex: 1,
        filter: true,
        sortable: false,
        resizable: true,
        suppressMovable: true,
      },
      // {
      //   sortable: false,
      //   filter: false,
      //   editable: false,
      //   width: 35,
      //   cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
      //   cellRenderer: AgButtonCellListComponent<ContactPerson>,
      //   cellRendererParams: {
      //     buttons: [
      //       {
      //         button: {
      //           icon: (item: ContactPerson) => (item?.keycloakid ? faShareSquare : faUserPlus),
      //           tooltip: (item: ContactPerson) =>
      //             item?.keycloakid
      //               ? this.translateService.instant('buttons.detail', {
      //                   name: this.translateService.instant('collections.users.singular'),
      //                 })
      //               : this.translateService.instant('buttons.create', {
      //                   name: this.translateService.instant('collections.users.singular'),
      //                 }),
      //           onClick: this.onHandleUser.bind(this),
      //           disabled: (item: ContactPerson) => {
      //             const userPermission = item.keycloakid
      //               ? this.hasPermission(
      //                   'read',
      //                   this.globalRolePermissionsParams(this.permissionsService.user, 'users'),
      //                 )
      //               : this.hasPermission(
      //                   'write',
      //                   this.globalRolePermissionsParams(this.permissionsService.user, 'users'),
      //                 );

      //             const companyPermission = this.hasPermission(
      //               'write',
      //               this.globalRolePermissionsParams(this.permissionsService.user, 'companyProfile'),
      //             );

      //             return this.mode === 'company-admin' || !companyPermission || !userPermission;
      //           },
      //         },
      //       },
      //     ],
      //   },
      // },
      {
        colId: 'edit',
        sortable: false,
        filter: false,
        editable: false,
        maxWidth: 45,
        cellStyle: { 'text-overflow': 'clip' },
        cellRenderer: AgEditCellComponent,
        cellRendererParams: {
          context: {
            firstField: 'voornaam',
            isDialog: true,
            componentContext: this,
            disabled: () =>
              !this.hasPermission(
                'write',
                this.globalRolePermissionsParams(this.permissionsService.user, 'companyProfile'),
              ),
          },
        },
      },
      {
        colId: 'delete',
        sortable: false,
        filter: false,
        editable: false,
        width: 35,
        cellStyle: { display: 'flex', 'align-items': 'center', 'justify-content': 'center' },
        cellRenderer: AgButtonCellListComponent<ContactPerson>,
        cellRendererParams: {
          buttons: [
            {
              button: {
                icon: faTrash,
                tooltip: this.translateService.instant('buttons.delete', {
                  name: this.translateService.instant('collections.persons.singular'),
                }),
                color: '#D32F2F',
                onClick: this.onDeletePerson.bind(this),
                disabled: () =>
                  !this.hasPermission(
                    'delete',
                    this.globalRolePermissionsParams(this.permissionsService.user, 'companyProfile'),
                  ),
              },
              confirmDialog: {
                key: 'company-confirm-dialog',
                identifier: (item: ContactPerson) => `${item.voornaam} ${item.achternaam}`,
                header: this.translateService.instant('confirmations.delete.header', {
                  name: this.translateService.instant('collections.persons.singular'),
                }),
                defaultFocus: 'reject',

                acceptIcon: 'fa fa-trash fa-fw',
                acceptLabel: this.translateService.instant('buttons.delete', {
                  name: ' ',
                }),
                acceptLabelStyleClass: 'p-button-danger',

                rejectIcon: 'fa fa-times',
                rejectLabel: this.translateService.instant('buttons.cancel'),
                rejectButtonStyleClass: 'p-button-secondary',
              },
            },
          ],
        },
      },
    ];
    return colDefs;
  }

  public onPersonGridApiChange(gridApi: GridApi<ContactPerson>) {
    this.personGridApi = gridApi;
  }

  public onHandleUser(
    value: ContactPerson,
    params: AgButtonCellParams<ContactPerson>,
    rowNode: IRowNode<ContactPerson>,
  ) {
    if (value?.keycloakid) {
      this.router.navigate(['users', 'company-profile', 'update', value?.keycloakid]);
    } else {
      this.router.navigate(['users', 'company-profile', 'create', 'contactperson', rowNode.rowIndex]);
    }
  }

  public onDeletePerson(
    value: ContactPerson,
    params: AgButtonCellParams<ContactPerson>,
    rowNode: IRowNode<ContactPerson>,
  ) {
    const index = rowNode.rowIndex;
    if (index !== null && index !== undefined) {
      this.currentCompany.contactpersonen.splice(index, 1);
      this.companyProfileService
        .updateResourceById(this.currentCompany.id, { body: this.currentCompany })
        .subscribe(() => {
          this.personGridApi.applyTransaction({ remove: [value] });
        });
    }
  }

  public submitForm() {
    if (!this.formGroup.valid) {
      FormUtils.markAllAsDirtyAndTouched(this.formGroup);
      FormUtils.markAllAsDirtyAndTouched(this.reportColorFormGroup);
      return;
    }

    this.companyId$.subscribe((companyId) => {
      if (this.mode === 'company-profile') {
        this.currentCompany.naam = this.formGroup.value['naam'];
        this.currentCompany.adres = {
          email: this.formGroup.value['email'],
          plaats: this.formGroup.value['place'],
          postcode: this.formGroup.value['postcode'],
          straat: this.formGroup.value['address'],
          telefoon: this.formGroup.value['tlf'],
          website: this.formGroup.value['website'],
        };
        this.currentCompany.postadres = {
          plaats: this.formGroup.value['mailingPlace'],
          postcode: this.formGroup.value['mailingPostcode'],
          straat: this.formGroup.value['mailingAddress'],
        };
        this.currentCompany.logo = this.formGroup.value['logo'];
        this.currentCompany.modules = this.formGroup.value['modules'];
        this.currentCompany.categorien = this.formGroup.value['categorien'];
        this.currentCompany.licentie = this.formGroup.getRawValue()['licentie'];
        this.currentCompany.isopdrachtgever = this.formGroup.value['isopdrachtgever'];
        if (this.reportColorFormGroup.value.titleColor && this.reportColorFormGroup.value.titleColor != titleColor) {
          this.currentCompany.titleColor = this.reportColorFormGroup.value.titleColor;
        }

        if (
          this.reportColorFormGroup.value.subtitleColor &&
          this.reportColorFormGroup.value.subtitleColor != subtitleColor
        ) {
          this.currentCompany.subtitleColor = this.reportColorFormGroup.value.subtitleColor;
        }

        if (this.tableHeaderColor != tableHeaderColor) {
          this.currentCompany.tableHeaderColor = this.tableHeaderColor;
        }

        if (this.fontColor != fontColor) {
          this.currentCompany.fontColor = this.fontColor;
        }

        this.companyService.updateItemById(this.currentCompany);
      } else if (this.mode === 'company-admin') {
        if (companyId) {
          this.currentCompany.naam = this.formGroup.value['naam'];
          this.currentCompany.adres = {
            email: this.formGroup.value['email'],
            plaats: this.formGroup.value['place'],
            postcode: this.formGroup.value['postcode'],
            straat: this.formGroup.value['address'],
            telefoon: this.formGroup.value['tlf'],
            website: this.formGroup.value['website'],
          };
          this.currentCompany.postadres = {
            plaats: this.formGroup.value['mailingPlace'],
            postcode: this.formGroup.value['mailingPostcode'],
            straat: this.formGroup.value['mailingAddress'],
          };
          this.currentCompany.logo = this.formGroup.value['logo'];
          this.currentCompany.modules = this.formGroup.value['modules'];
          this.currentCompany.categorien = this.formGroup.value['categorien'];
          this.currentCompany.licentie = this.formGroup.getRawValue()['licentie'];
          this.currentCompany.isopdrachtgever = this.formGroup.value['isopdrachtgever'];
          if (this.reportColorFormGroup.value.titleColor && this.reportColorFormGroup.value.titleColor != titleColor) {
            this.currentCompany.titleColor = this.reportColorFormGroup.value.titleColor;
          }
          if (
            this.reportColorFormGroup.value.subtitleColor &&
            this.reportColorFormGroup.value.subtitleColor != subtitleColor
          ) {
            this.currentCompany.subtitleColor = this.reportColorFormGroup.value.subtitleColor;
          }
          if (this.tableHeaderColor != tableHeaderColor) {
            this.currentCompany.tableHeaderColor = this.tableHeaderColor;
          }
          if (this.fontColor != fontColor) {
            this.currentCompany.fontColor = this.fontColor;
          }

          this.companyService.updateItemById(this.currentCompany);
        } else {
          this.currentCompany = {
            naam: this.formGroup.value['naam'],
            adres: {
              email: this.formGroup.value['email'],
              plaats: this.formGroup.value['place'],
              postcode: this.formGroup.value['postcode'],
              straat: this.formGroup.value['address'],
              telefoon: this.formGroup.value['tlf'],
              website: this.formGroup.value['website'],
            },
            postadres: {
              plaats: this.formGroup.value['mailingPlace'],
              postcode: this.formGroup.value['mailingPostcode'],
              straat: this.formGroup.value['mailingAddress'],
            },
            logo: this.formGroup.value['logo'],
            modules: this.formGroup.value['modules'],
            categorien: this.formGroup.value['categorien'],
            licentie: this.formGroup.getRawValue()['licentie'],
            isopdrachtgever: this.formGroup.value['isopdrachtgever'],
            titleColor:
              this.reportColorFormGroup.value.titleColor != titleColor
                ? this.reportColorFormGroup.value.titleColor
                : undefined,
            tableHeaderColor: this.tableHeaderColor != tableHeaderColor ? this.tableHeaderColor : undefined,
            subtitleColor:
              this.reportColorFormGroup.value.subtitleColor != subtitleColor
                ? this.reportColorFormGroup.value.subtitleColor
                : undefined,
            fontColor: this.fontColor != fontColor ? this.fontColor : undefined,
            contactpersonen: [],
          } as unknown as Company;
          this.companyService.createItem(this.currentCompany);
        }
      }

      if (this.permissionsService.licence) {
        if (!this.permissionsService.licence.integrations) {
          this.permissionsService.licence.integrations = {
            AFAS: {
              apiToken: '',
              administrationId: '',
            },
            EXACT: {
              apiKey: '',
              baseUrl: '',
            },
            ITRIS: {
              username: '',
              password: '',
            },
          };
        }

        this.permissionsService.licence.integrations.AFAS = {
          apiToken: this.formGroup.value['afasToken'],
          administrationId: this.formGroup.value['afasId'],
        };

        this.permissionsService.licence.integrations.EXACT = {
          apiKey: this.formGroup.value['exactApiKey'],
          baseUrl: this.formGroup.value['exactUrl'],
        };
        this.permissionsService.licence.integrations.ITRIS = {
          username: this.formGroup.value['itrisUsername'],
          password: this.formGroup.value['itrisPassword'],
        };
        this.permissionsService.licence.uploadReportsIntegration = this.formGroup.value['uploadReportsIntegration'];
        this.licenseService.updateIntegrations(this.permissionsService.licence).subscribe();
      }
    });
  }

  public async uploadLogo(event: any, fileUpload: FileUpload) {
    this.uploadingLogo.set(true);
    const result: any = await this.readFileFromEvent(event);
    const uuid = uuidv4();
    const type = result
      .split(',')[0]
      .substring(5)
      .substring(0, result.split(',')[0].substring(5).length - 7);
    const base64Content = result.split(',')[1];

    const fileDocument = new FileDocument();

    const { width, height } = await this.getImageResolution(event.files[0]);

    if (width > 1000 || height > 1000) {
      this.uploadingLogo.set(false);
      fileUpload.clear();
      ToastUtils.showError(this.toastService, this.translateService.instant('companyProfile.maxLogoSizeError'));
      return;
    }

    this.imageSrc = result;

    fileDocument.uniekecode = uuid;
    fileDocument.naam = 'logo-' + uuid;
    fileDocument.inhoudtype = type;
    fileDocument.content = base64Content;

    if (this.formGroup.value['logo']) {
      if (this.companyLogoObj) {
        this.companyLogoObj.content = base64Content;
        this.companyLogoObj.inhoudtype = type;
      }
      this.restService.put(`/document/${this.formGroup.value['logo']}`, this.companyLogoObj).subscribe({
        next: () => {
          this.uploadingLogo.set(false);
          this.toastService.add({
            key: 'company-profile',
            severity: 'success',
            summary: this.translateService.instant('savedCompanyProfile'),
          });
          fileUpload.clear();
        },
        error: (err: RgsHttpErrorResponse) => {
          this.uploadingLogo.set(false);
          fileUpload.clear();
          ToastUtils.showError(this.toastService, null, err.error);
        },
      });
    } else {
      this.documentService.uploadPhoto(fileDocument).subscribe({
        next: (value) => {
          this.uploadingLogo.set(false);
          this.toastService.add({
            key: 'company-profile',
            severity: 'success',
            summary: this.translateService.instant('savedCompanyProfile'),
          });
          this.formGroup.patchValue({ logo: value.uniekecode });
          this.submitForm();
          fileUpload.clear();
        },
        error: (err: RgsHttpErrorResponse) => {
          this.uploadingLogo.set(false);
          fileUpload.clear();
          ToastUtils.showError(this.toastService, null, err.error);
        },
      });
    }
  }

  private async getImageResolution(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
        URL.revokeObjectURL(url);
        resolve(dimensions);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject();
      };
      img.src = url;
    });
  }

  public deleteLogo(fileUpload: FileUpload) {
    this.formGroup.patchValue({ logo: '' });
    this.submitForm();
    fileUpload.clear();
    this.imageSrc = undefined;
  }

  public async uploadCompanyBackground(event: any, fileUpload: FileUpload) {
    const file: File = event.files?.[0];
    if (!file || !this.currentCompany?.id) {
      fileUpload.clear();
      return;
    }

    const { width, height } = await this.getImageResolution(file);

    const ratio = width / height;
    if (Math.abs(ratio - this.A4_PORTRAIT_ASPECT_RATIO) > this.A4_ASPECT_TOLERANCE) {
      fileUpload.clear();
      this.toastService.add({
        key: 'company-profile',
        severity: 'warn',
        life: 4000,
        summary: this.translateService.instant('companyProfile.companyBackground.a4ErrorTitle'),
        detail: this.translateService.instant('companyProfile.companyBackground.a4Error'),
      });
      return;
    }

    if (width < this.A4_MIN_WIDTH_PX || height < this.A4_MIN_HEIGHT_PX) {
      fileUpload.clear();
      this.toastService.add({
        key: 'company-profile',
        severity: 'warn',
        life: 4000,
        summary: this.translateService.instant('companyProfile.companyBackground.resolutionErrorTitle'),
        detail: this.translateService.instant('companyProfile.companyBackground.resolutionError', {
          width: this.A4_MIN_WIDTH_PX,
          height: this.A4_MIN_HEIGHT_PX,
        }),
      });
      return;
    }

    const dataUrl = (await this.readFileAsDataUrl(file)) as string;

    this.uploadingCompanyBackground.set(true);
    const request$ = this.currentCompany.companyBackgroundImageId
      ? this.companyService.updateCompanyBackgroundImage(this.currentCompany.id, file)
      : this.companyService.uploadCompanyBackgroundImage(this.currentCompany.id, file);

    request$.subscribe({
      next: (company: Company) => {
        this.uploadingCompanyBackground.set(false);
        this.currentCompany.companyBackgroundImageId = company.companyBackgroundImageId;
        this.companyBackgroundImageSrc.set(dataUrl);
        this.toastService.add({
          key: 'company-profile',
          severity: 'success',
          summary: this.translateService.instant('savedCompanyProfile'),
        });
        fileUpload.clear();
      },
      error: (err: RgsHttpErrorResponse) => {
        this.uploadingCompanyBackground.set(false);
        fileUpload.clear();
        ToastUtils.showError(this.toastService, null, err.error);
      },
    });
  }

  public deleteCompanyBackground() {
    if (!this.currentCompany?.id) {
      return;
    }
    this.deletingCompanyBackground.set(true);
    this.companyService.deleteCompanyBackgroundImage(this.currentCompany.id).subscribe({
      next: () => {
        this.deletingCompanyBackground.set(false);
        this.currentCompany.companyBackgroundImageId = null;
        this.companyBackgroundImageSrc.set(undefined);
        this.toastService.add({
          key: 'company-profile',
          severity: 'success',
          summary: this.translateService.instant('savedCompanyProfile'),
        });
      },
      error: (err: RgsHttpErrorResponse) => {
        this.deletingCompanyBackground.set(false);
        ToastUtils.showError(this.toastService, null, err.error);
      },
    });
  }

  private readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(file);
    });
  }

  private readFileFromEvent(event: any) {
    return new Promise((resolve) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(event.files[0]);
      fileReader.onloadend = () => {
        const result: any = fileReader.result;
        resolve(result);
      };
    });
  }
  public toggleCard() {
    this.toggleButtonTitle = this.showForm ? 'Profile form' : 'Contact Persons';
    this.showForm = !this.showForm;
  }

  public submitContactPersonForm() {
    if (!this.contactPersonFormGroup.valid) {
      FormUtils.markAllAsDirtyAndTouched(this.contactPersonFormGroup);
      return;
    }
    this.savingContactPerson.set(true);
    if (this.currentEditingRow?.data) {
      this.saveEditedRow();
    } else {
      this.addNewContactPersonOnSubmit();
    }
  }

  public isSaveButtonDisabled() {
    return !this.contactPersonFormGroup.valid || this.savingContactPerson();
  }

  private saveEditedRow() {
    const indexOfContact = findIndex(this.currentCompany.contactpersonen, this.currentEditingRow.data);
    this.currentCompany.contactpersonen[indexOfContact] = {
      ...this.currentEditingRow.data,
      ...this.contactPersonFormGroup.value,
    };
    const role = this.currentEditingRow.data?.companyRoleId;
    if (role) {
      this.currentCompany.contactpersonen[indexOfContact] = {
        ...this.currentCompany.contactpersonen[indexOfContact],
        companyRole: {
          id: role,
        },
      } as any;
    }

    this.companyProfileService.updateResourceById(this.currentCompany.id, { body: this.currentCompany }).subscribe({
      next: (company: Company) => {
        this.handleContactPersonHide();
        this.currentCompany = company as any as Company;
        this.gridInstance.gridApi.setGridOption('rowData', this.currentCompany.contactpersonen);
        this.toastService.add({
          severity: 'success',
          summary: this.translateService.instant('savedCompanyProfile'),
        });
      },
      error: () => {
        this.savingContactPerson.set(false);
        this.toastService.add({
          severity: 'error',
          detail: 'Error saving contact person',
        });
      },
    });
  }

  private addNewContactPersonOnSubmit() {
    const newContactPerson: any = {
      account: this.contactPersonFormGroup.value['account'],
      achternaam: this.contactPersonFormGroup.value['achternaam'],
      email: this.contactPersonFormGroup.value['email'],
      functie: this.contactPersonFormGroup.value['functie'],
      loginnaam:
        this.contactPersonFormGroup.value['voornaam'].toLowerCase() +
        this.contactPersonFormGroup.value['achternaam'].toLowerCase(),
      mobiel: this.contactPersonFormGroup.value['mobiel'],
      status: 'A',
      voornaam: this.contactPersonFormGroup.value['voornaam'],
    };
    this.gridInstance.gridApi.applyTransaction({ add: [newContactPerson] });
    this.currentCompany.contactpersonen.push(newContactPerson);
    this.companyProfileService.updateResourceById(this.currentCompany.id, { body: this.currentCompany }).subscribe(
      () => {
        this.handleContactPersonHide();
        this.toastService.add({
          severity: 'success',
          summary: this.translateService.instant('savedCompanyProfile'),
        });
      },
      () => {
        this.handleContactPersonHide();
        this.toastService.add({
          severity: 'error',
          detail: 'Error saving contact person',
        });
      },
    );
  }

  public handleContactPersonHide() {
    this.savingContactPerson.set(false);
    this.contactPersonFormGroup.reset();
    this.contactPersonDialogOpen = false;
  }
  public addNewItem() {
    this.contactPersonDialogOpen = true;
  }

  public openEditDialog(gridParams: ICellRendererParams) {
    this.currentEditingRow = gridParams;
    this.contactPersonDialogOpen = true;
    this.contactPersonFormGroup.patchValue(gridParams.data);
  }

  public get userRoles() {
    const tokenParsed = this.keycloak.tokenParsed;
    return ArrayUtils.ensureArray(tokenParsed?.realm_access?.roles);
  }

  public get hasPermission() {
    return this.permissionsService.hasPermission.bind(this.permissionsService);
  }

  public get globalRolePermissionsParams() {
    return globalRolePermissionsParams;
  }
  public resetColors() {
    this.reportColorFormGroup.patchValue({
      titleColor: titleColor,
      subtitleColor: subtitleColor,
    });
    this.tableHeaderColor = tableHeaderColor;
    this.fontColor = fontColor;
    this.currentCompany.titleColor = undefined;
    this.currentCompany.subtitleColor = undefined;
    this.currentCompany.tableHeaderColor = undefined;
    this.currentCompany.fontColor = undefined;
  }
}
